import { useState } from 'react';
import type { FC } from 'react';
import {
  VStack,
  HStack,
  Spinner,
  Center,
  Input,
  Button,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageHeading, Panel } from '@bemap/ui';
import {
  OverviewMetrics as OverviewMetricsComponent,
  useOverviewMetrics,
} from '@bemap/crm-microfrontends';
import type { OverviewMetricsData } from '@bemap/crm-microfrontends';
import { getOverviewMetrics } from '@/services/crm-service';

const CrmOverviewPage: FC = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const fetchMetrics = async (options?: { startDate?: string; endDate?: string }): Promise<OverviewMetricsData> => {
    const res = await getOverviewMetrics(options?.startDate, options?.endDate);
    const data = (res.message ?? null) as OverviewMetricsData | null;
    if (data) return data;

    return {
      period_start: options?.startDate || '',
      period_end: options?.endDate || '',
      total_leads: 0,
      working_leads: 0,
      funnel_breakdown: [],
      stage_totals: {
        to_contact: 0,
        working: 0,
        converted: 0,
      },
      converted_leads: 0,
      discarded_leads: 0,
      conversion_rate: 0,
      total_deal_value: 0,
      pipeline_value: 0,
      average_deal_value: 0,
      timeline: [],
    };
  };

  const { metrics, loading, refetch } = useOverviewMetrics(fetchMetrics, {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  const handleApplyFilter = () => {
    refetch();
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    refetch();
  };

  return (
    <VStack w="100%" gap={4} align="stretch">
      <PageHeading title={t('crm.overview') || 'CRM Overview'} />
      <Panel>
        <VStack align="stretch" gap={6} p={6}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            align={{ base: 'stretch', md: 'flex-end' }}
            flexWrap="wrap"
          >
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="Start Date"
              maxW={{ base: '100%', md: '200px' }}
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="End Date"
              maxW={{ base: '100%', md: '200px' }}
            />
            <HStack spacing={3} align="center">
              <Button onClick={handleApplyFilter} colorScheme="blue">
                {t('actions.apply') || 'Apply'}
              </Button>
              {(startDate || endDate) && (
                <Button variant="outline" onClick={handleClear} borderColor={borderColor}>
                  {t('actions.clear') || 'Clear'}
                </Button>
              )}
            </HStack>
          </Flex>
          {loading ? (
            <Center h="240px">
              <Spinner size="xl" />
            </Center>
          ) : (
            <OverviewMetricsComponent
              metrics={metrics}
              loading={loading}
              labels={{
                title: t('crm.overview_metrics_title') || 'Overview metrics',
                rangeAllTime: t('crm.overview_all_time') || 'All time',
                totalLeads: t('crm.total_leads') || 'Total leads',
                workingLeads: t('crm.working_leads') || 'Working leads',
                convertedLeads: t('crm.converted_leads') || 'Converted',
                discardedLeads: t('crm.discarded_leads') || 'Discarded',
                conversionRate: t('crm.conversion_rate') || 'Conversion rate',
                totalDealValue: t('crm.total_deal_value') || 'Total deal value',
                pipelineValue: t('crm.pipeline_value') || 'Pipeline value',
                averageDealValue: t('crm.average_deal_value') || 'Average deal',
                timelineHeading: t('crm.timeline_heading') || 'Lead volume over time',
                timelineEmpty: t('crm.timeline_empty') || 'No timeline data yet',
                funnelBreakdownTitle: t('crm.funnel_breakdown_title') || 'Funnel breakdown',
                funnelBreakdownEmpty: t('crm.funnel_breakdown_empty') || 'No funnels with leads yet',
                funnelPieTitle: t('crm.funnel_pie_title') || 'Distribution by funnel',
                funnelPieEmpty: t('crm.funnel_pie_empty') || 'No leads to display',
                stageToContact: t('crm.stage_to_contact') || 'To contact',
                stageWorking: t('crm.stage_working') || 'Working',
                stageConverted: t('crm.stage_converted') || 'Converted',
                funnelColumnFunnel: t('crm.funnel_column_funnel') || 'Funnel',
                funnelColumnToContact: t('crm.funnel_column_to_contact') || 'To contact',
                funnelColumnWorking: t('crm.funnel_column_working') || 'Working',
                funnelColumnConverted: t('crm.funnel_column_converted') || 'Converted',
                funnelColumnTotal: t('crm.funnel_column_total') || 'Total',
              }}
            />
          )}
        </VStack>
      </Panel>
    </VStack>
  );
};

export default CrmOverviewPage;

